import type {
  StatTypeData,
  StatTypeRequestData,
  StatTypesQueryParams
} from '@library/types/statType';
import { useCallback, useMemo } from 'react';
import {
  useCreateStatTypeMutation,
  useGetStatTypesQuery,
  useUpdateStatTypeMutation
} from '@bo-services/statTypesApi';
import type { LeaguesList } from '@library/types/leagues';
import { formatDate } from '@library/component-utils/dateTime';
import { mapNamesToIds } from '@library/component-utils/leagues';

export const useStatTypes = ({
  statTypesQueryParams,
  leaguesList,
  currentItemId,
  setSubmitMessage
}: {
  statTypesQueryParams: StatTypesQueryParams;
  leaguesList: LeaguesList;
  currentItemId: string;
  setSubmitMessage: (message: string) => void;
}) => {
  const {
    data: response,
    isError,
    isLoading,
    isSuccess
  } = useGetStatTypesQuery(statTypesQueryParams);

  const [createStatType] = useCreateStatTypeMutation();
  const [updateStatType] = useUpdateStatTypeMutation();

  const statTypesData = useMemo(() => {
    return response?.data.map((statType) => {
      return {
        createdAt: formatDate({ date: statType.attributes.created_at }),
        earlySettleBuffer:
          String(statType.attributes.early_settle_buffer) || '',
        earlySettleBufferDelay:
          String(statType.attributes.early_settle_buffer_delay) || '',
        postGameSettleBuffer:
          String(statType.attributes.post_game_settle_buffer) || '',
        postGameSettleBufferDelay:
          String(statType.attributes.post_game_settle_buffer_delay) || '',
        id: statType.id,
        leagueNames: statType.relationships.leagues.data
          .map((league) => {
            return response.included.find((l) => l.id === league.id)?.attributes
              .name;
          })
          .join(', '),
        leagueIds: statType.relationships.leagues.data
          .map((league) => league.id)
          .join(', '),
        lfgIgnoredLeagues: statType.attributes.lfg_ignored_leagues?.join(', '),
        name: statType.attributes.name,
        postGameFinalizationBuffer:
          String(statType.attributes.post_game_finalization_buffer) || '',
        rank: statType.attributes.rank || '',
        selectorLength: String(statType.attributes.selector_length) || '',
        selectorStart: String(statType.attributes.selector_start) || '',
        shortName: statType.attributes.short_name,
        statSelectors: statType.attributes.stat_selectors?.join(', '),
        updatedAt: formatDate({ date: statType.attributes.updated_at })
      };
    }) as Array<StatTypeData>;
  }, [response]);

  const mapStatTypeDataToRequest = useCallback(
    (statTypeFormData: Partial<StatTypeData>): StatTypeRequestData => {
      const {
        earlySettleBuffer,
        earlySettleBufferDelay,
        postGameSettleBuffer,
        postGameSettleBufferDelay,
        leagueNames,
        lfgIgnoredLeagues,
        name,
        postGameFinalizationBuffer,
        rank,
        scoredPeriods,
        selectorLength,
        selectorStart,
        shortName,
        statSelectors
      } = statTypeFormData;
      return {
        name: name || '',
        short_name: shortName || '',
        rank: Number(rank) || 0,
        early_settle_buffer: Number(earlySettleBuffer) || 0,
        early_settle_buffer_delay: Number(earlySettleBufferDelay) || 0,
        post_game_settle_buffer: Number(postGameSettleBuffer) || 0,
        post_game_settle_buffer_delay: Number(postGameSettleBufferDelay) || 0,
        post_game_finalization_buffer: Number(postGameFinalizationBuffer) || 0,
        lfg_ignored_leagues: lfgIgnoredLeagues
          ? lfgIgnoredLeagues.split(',').map(Number)
          : [],
        scored_periods: scoredPeriods
          ? scoredPeriods.split(',').map(Number)
          : [],
        stat_selectors: statSelectors ? statSelectors.split(',') : [],
        selector_length: Number(selectorLength) || 0,
        selector_start: Number(selectorStart) || 0,
        league_ids: leagueNames
          ? mapNamesToIds(leaguesList, leagueNames.split(','))
          : []
      };
    },
    [leaguesList]
  );

  const handleUpdate = useCallback(
    async (formData: Partial<StatTypeData>) => {
      await updateStatType({
        id: currentItemId,
        data: mapStatTypeDataToRequest(formData)
      })
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Stat Type updated');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't update Stat Type - ${error}`);
        });
    },
    [updateStatType, currentItemId, mapStatTypeDataToRequest, setSubmitMessage]
  );

  const handleCreate = useCallback(
    async (formData: Partial<StatTypeData>) => {
      await createStatType(mapStatTypeDataToRequest(formData))
        .unwrap()
        .then((payload) => {
          // eslint-disable-next-line no-console
          console.log('fulfilled', payload);
          setSubmitMessage('Stat Type created');
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('rejected', error);
          setSubmitMessage(`Couldn't create Stat Type - ${error}`);
        });
    },
    [createStatType, mapStatTypeDataToRequest, setSubmitMessage]
  );

  return {
    createStatType: handleCreate,
    isError,
    isLoading,
    isSuccess,
    mapStatTypeDataToRequest,
    meta: response?.meta || {},
    page: response?.meta.current_page,
    statTypes: statTypesData || [],
    totalPages: response?.meta.total_pages || 0,
    updateStatType: handleUpdate
  };
};
